import { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import Button from 'Components/Atoms/Button/Button';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import Select from 'Components/Atoms/Select/Select';
import Checkbox from 'Components/Atoms/Checkbox/Checkbox';
import { Trans, useTranslation } from 'react-i18next';

const VALUE_NONE = 'none';

export default function ImportFileMapper(props) {
    const {
        onClose,
        importId,
        onSubmit,
        columns,
        onChangeProcessing,
        processing,
        title,
        description,
        buttonText,
        mapService,
        fields: initialFields,
    } = props;

    const [error, setError] = useState(null);

    const { t } = useTranslation('common');

    const [fields, setFields] = useState(() => {
        return initialFields.map((item) => ({
            ...item,
            value: VALUE_NONE,
            deduplicationColumnsValue: false,
        }));
    });

    const { showUnknownError } = useErrors();

    const handleSubmit = async () => {
        const allRequiredHasValue = fields.every((item) => !item.required || item.value !== VALUE_NONE);

        if (!allRequiredHasValue) {
            setError(t('import-error-required-fields'));
            return;
        }

        onChangeProcessing(true);
        setError(null);

        try {
            const deduplicationColumns = fields.filter((item) => item.deduplicationColumnsValue).map((item) => item.id);

            const mappedColumns = fields.reduce(
                (acc, item) => {
                    if (item.value !== VALUE_NONE) {
                        acc[item.id] = columns.findIndex((column) => column.id === item.value);
                    } else {
                        acc[item.id] = null;
                    }

                    return acc;
                },
                {
                    deduplication_columns: deduplicationColumns,
                },
            );

            const response = await mapService(importId, mappedColumns);
            onSubmit(response);
        } catch (error) {
            showUnknownError(error);
            onClose();
        } finally {
            onChangeProcessing(false);
        }
    };

    const noneOption = {
        value: VALUE_NONE,
        label: `- ${t('column-none')} -`,
    };

    const columnOptions = columns.map((item) => ({
        value: item.id,
        label: (
            <div>
                <div>{item.id}</div>
                <div className="text-xs opacity-50">{'"' + item.sample + '"'}</div>
            </div>
        ),
    }));

    const options = [noneOption, ...columnOptions];

    const handleChange = (name, value) => {
        setError(null);

        setFields((prev) => {
            const newFields = prev.map((field) => {
                if (field.id === name) {
                    if (value === VALUE_NONE) {
                        return {
                            ...field,
                            deduplicationColumnsValue: false,
                            value,
                        };
                    }

                    if (field.unique) {
                        return {
                            ...field,
                            deduplicationColumnsValue: true,
                            value,
                        };
                    }

                    return {
                        ...field,
                        value,
                    };
                }

                return field;
            });

            return newFields;
        });
    };

    const handleChangeCheckbox = (ev) => {
        const { name, checked } = ev.target;

        setFields((prev) => {
            const newFields = prev.map((field) => {
                if (`checkbox-${field.id}` === name) {
                    return {
                        ...field,
                        deduplicationColumnsValue: checked,
                    };
                }

                return field;
            });

            return newFields;
        });
    };

    return (
        <div className="flex flex-col h-full">
            <div className="grow">
                <div className="mb-12">
                    {title && <h4>{title}</h4>}
                    <div className="text-gray-500">{description}</div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-6 items-stretch">
                    <div className="uppercase font-semibold text-2xs tracking-widest text-gray-500">
                        {t('mapper-data')}
                    </div>
                    <div className="uppercase font-semibold text-2xs tracking-widest text-gray-500">
                        {t('mapper-column')}
                    </div>
                    {fields.map((item) => (
                        <Fragment key={item.id}>
                            <div className="border border-gray-300 rounded bg-gray-50 text-dark-blue px-3 py-2 overflow-hidden text-sm">
                                {item.label}
                                {item.required && (
                                    <>
                                        <span className="text-danger"> *</span>
                                        <div className="text-xs opacity-50">{t('required')}</div>
                                    </>
                                )}
                            </div>

                            <Select
                                options={options}
                                value={item.value}
                                className="w-full"
                                onChange={(value) => handleChange(item.id, value)}
                            />
                            {item.deduplicationColumns && item.value !== VALUE_NONE && (
                                <div className="col-span-2 pb-1">
                                    <Checkbox
                                        checked={item.deduplicationColumnsValue}
                                        name={`checkbox-${item.id}`}
                                        onChange={handleChangeCheckbox}
                                        disabled={item.unique}
                                    >
                                        <Trans t={t} i18nKey="import-deduplication">
                                            Do not import with <strong>{{ column: item.label }}</strong> repeated
                                        </Trans>
                                    </Checkbox>
                                </div>
                            )}
                        </Fragment>
                    ))}
                </div>
            </div>
            {error && <div className="text-danger text-sm py-3 text-center">{error}</div>}
            <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                <div className="flex gap-2">
                    <Button isBusy={processing} variant="primary" className="grow" onClick={handleSubmit}>
                        {buttonText}
                    </Button>
                    <Button className="grow" onClick={onClose}>
                        {t('cancel')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

ImportFileMapper.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onChangeProcessing: PropTypes.func.isRequired,
    processing: PropTypes.bool,
    columns: PropTypes.array,
    importId: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    buttonText: PropTypes.string,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            required: PropTypes.bool,
            deduplicationColumns: PropTypes.bool,
            label: PropTypes.string,
        }),
    ).isRequired,
    mapService: PropTypes.func.isRequired,
};
